import { useRef, useState, useEffect } from 'react'
import { ReactComponent as RestartIcon } from '../../../../images/icons/restart.svg'
import { PaperTypes, Services, PAPER_TYPES } from '../../../../lib/const'
import { useLabOrderDetailsStore } from '../../../../stores'

const LineItemStatuses = [
  { code: 'open', label: 'Open' },
  { code: 'paid', label: 'Paid' },
  { code: 'printing', label: 'Printing' },
  { code: 'matting', label: 'Matting' },
  { code: 'mounting', label: 'Mounting' },
  { code: 'framing', label: 'Framing' },
  { code: 'canvas_production', label: 'Canvas Prod' },
  { code: 'album_production', label: 'Album Prod' },
  { code: 'reprint', label: 'Reprint' },
  { code: 'ready_to_ship', label: 'Ready To Ship' },
  { code: 'shipped', label: 'Shipped' },
  { code: 'on_hold', label: 'On Hold' },
  { code: 'cancelled', label: 'Cancelled' },
]

const getLineItemService = (lineItem) => {
  const attributes = lineItem?.attributes

  if (lineItem?.type === 'album_order_detail') return 'album'
  if (lineItem?.type === 'multi_openings_line_item') return 'multi-opening'
  if (attributes?.photographType === 'prints') return 'test_print'
  if (attributes?.photographType === 'artist-proofs') return 'artist_proof'
  if (attributes?.photographType === 'multi-opening') return 'multi-opening'

  const category = attributes?.finishingCategoryName?.toLowerCase() ?? ''
  if (category.includes('mat')) return 'mat'
  if (category.includes('mount')) return 'mount'
  if (category.includes('fram')) return 'frame'
  if (category.includes('canvas')) return 'canvas'

  return category
}

const getLineItemPaper = (lineItem) =>
  lineItem?.attributes?.paperName ?? lineItem?.attributes?.albumPaperName ?? ''

const LineItemFilters = () => {
  const {
    order,
    originalLineItems,
    multiOpeningLineItems,
    albumOrderDetails,
    setFilteredLineItems,
    setFilteredMultiOpeningLineItems,
    setFilteredAlbumOrderDetails,
    selectedPapers,
    setSelectedPapers,
    selectedServices,
    setSelectedServices,
    selectedStatus,
    setSelectedStatus,
  } = useLabOrderDetailsStore();

  const [openDropdown, setOpenDropdown] = useState(null)
  const dropdownRefs = useRef([])

  const toggleDropdown = (dropdownId) => {
    setOpenDropdown(openDropdown === dropdownId ? null : dropdownId)
  }

  const closeDropdown = (e) => {
    if (!dropdownRefs.current.some((ref) => ref && ref.contains(e.target))) {
      setOpenDropdown(null)
    }
  }

  useEffect(() => {
    document.addEventListener('mousedown', closeDropdown)
    return () => {
      document.removeEventListener('mousedown', closeDropdown)
    }
  }, [])

  // Handle paper type checkbox changes
  const handlePaperCheckboxChange = (paperCode, isChecked) => {
    let newSelectedPapers = [...selectedPapers]
    if (isChecked) {
      newSelectedPapers.push(paperCode)
    } else {
      newSelectedPapers = newSelectedPapers.filter(code => code !== paperCode)
    }
    setSelectedPapers(newSelectedPapers)
    applyFilters(newSelectedPapers, selectedServices, selectedStatus)
  }

  // Handle service checkbox changes
  const handleServiceCheckboxChange = (serviceCode, isChecked) => {
    let newSelectedServices = [...selectedServices]
    if (isChecked) {
      newSelectedServices.push(serviceCode)
    } else {
      newSelectedServices = newSelectedServices.filter(code => code !== serviceCode)
    }
    setSelectedServices(newSelectedServices)
    applyFilters(selectedPapers, newSelectedServices, selectedStatus)
  }

  const handleStatusCheckboxChange = (statusCode, isChecked) => {
    let newSelectedStatuses = [...selectedStatus]
    if (isChecked) {
      newSelectedStatuses.push(statusCode)
    } else {
      newSelectedStatuses = newSelectedStatuses.filter((code) => code !== statusCode)
    }
    setSelectedStatus(newSelectedStatuses)
    applyFilters(selectedPapers, selectedServices, newSelectedStatuses)
  }

  // Apply combined filters
  const applyFilters = (papers, services, status) => {
    const orderAttributes = order?.data?.attributes ?? {}
    const filterItems = (items) => {
      let filteredItems = [...items]

      if (papers.length > 0) {
        filteredItems = filteredItems.filter(lineItem => {
          const paperCode = getLineItemPaper(lineItem)
          return papers.some(paper => paperCode.toLowerCase().includes(paper.toLowerCase()));
        })
      }

      if (services.length > 0) {
        filteredItems = filteredItems.filter(lineItem => {
          return services.some(service => {
            if (service === 'api') {
              return orderAttributes.apiOrder || orderAttributes.allServices?.includes('api_order')
            }
            if (service === 'boutique') {
              return orderAttributes.boutiqueShipping || orderAttributes.isBoutiqueOrder
                || orderAttributes.allServices?.includes('boutique')
            }
            return service === getLineItemService(lineItem)
          })
        })
      }
      if (status.length > 0) {
        filteredItems = filteredItems.filter(lineItem => {
          return status.includes(lineItem?.attributes?.status)
        })
      }

      return filteredItems
    }

    setFilteredLineItems(filterItems(originalLineItems))
    setFilteredMultiOpeningLineItems(filterItems(multiOpeningLineItems))
    setFilteredAlbumOrderDetails(filterItems(albumOrderDetails))
  }

  // Reset all filters
  const resetAllFilters = (resetType) => {
    if (resetType === 'resetPapers') {
      setSelectedPapers([]);
      applyFilters([], selectedServices, selectedStatus);
    } else if (resetType === 'resetServices') {
      setSelectedServices([]);
      applyFilters(selectedPapers, [], selectedStatus);
    } else if (resetType === 'resetStatuses') {
      setSelectedStatus([])
      applyFilters(selectedPapers, selectedServices, [])
    }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column' }}>
      <div className="grid-container-controls">
        <div id="w-node-_2563b34c-ae53-7719-a7c5-13ea3ede76a8-6be80a0f" className="form-block-18 w-form">
          <form id="email-form-6" name="email-form-6" data-name="Email Form 6" className="form-11-copy">
            <div className="div-block-98-copy"><label className="field-label-2-copy">Filter By:</label>
              <div className="div-block-358">
                <div
                  ref={(el) => {
                    dropdownRefs.current[2] = el
                  }}
                  data-hover="false"
                  data-delay="0"
                  className={`w-dropdown ${openDropdown === 2 ? 'w--open' : ''}`}
                >
                  <div
                    className="filter-select-field w-dropdown-toggle"
                    id="w-dropdown-toggle-status"
                    aria-controls="w-dropdown-list-status"
                    aria-haspopup="menu"
                    aria-expanded={openDropdown === 2}
                    role="button"
                    tabIndex="0"
                    onClick={() => toggleDropdown(2)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') toggleDropdown(2)
                    }}
                  >
                    <div>Line Item Status</div>
                    <div className="icon-2 w-icon-dropdown-toggle" aria-hidden="true" />
                  </div>
                  <nav
                    className={`dropdown-list service w-dropdown-list ${openDropdown === 2 ? 'w--open' : ''}`}
                    id="w-dropdown-list-status"
                    aria-labelledby="w-dropdown-toggle-status"
                    style={{ display: openDropdown === 2 ? 'block' : 'none' }}
                  >
                    <div className="form-block-filter w-form">
                      <div className="form-3">
                        {LineItemStatuses.map((status) => {
                          const isChecked = selectedStatus.includes(status.code)
                          return (
                            <div className="div-block-45" key={status.code}>
                              <label className="w-checkbox checkbox-field line-item-filter-checkbox">
                                <div
                                  className={`w-checkbox-input w-checkbox-input--inputType-custom checkbox ${
                                    isChecked ? 'w--redirected-checked' : ''
                                  }`}
                                />
                                <input
                                  type="checkbox"
                                  checked={isChecked}
                                  onChange={(e) =>
                                    handleStatusCheckboxChange(status.code, e.target.checked)
                                  }
                                  style={{ opacity: 0, position: 'absolute', zIndex: -1 }}
                                />
                                <span className="w-form-label">{status.label}</span>
                              </label>
                            </div>
                          )
                        })}
                      </div>
                    </div>
                    <button
                      type="button"
                      className="container-info-filter bg"
                      onClick={() => resetAllFilters('resetStatuses')}
                      style={{ border: 0, width: '100%', textAlign: 'left' }}
                    >
                      <div className="icon small right-margin x2 w-embed">
                        <RestartIcon />
                      </div>
                      <div>Reset All</div>
                    </button>
                  </nav>
                </div>
              </div>
            </div>
          </form>
        </div>

        <div className="w-layout-grid grid-filters">
          {/* Paper Type Dropdown */}
          <div
            ref={el => dropdownRefs.current[0] = el}
            data-hover="false"
            data-delay="0"
            className={`w-dropdown ${openDropdown === 0 ? 'w--open' : ''}`}
          >
            <div
              className="filter-select-field w-dropdown-toggle"
              id="w-dropdown-toggle-0"
              aria-controls="w-dropdown-list-0"
              aria-haspopup="menu"
              aria-expanded={openDropdown === 0}
              role="button"
              tabIndex="0"
              onClick={() => toggleDropdown(0)}
            >
              <div>Paper Type</div>
              <div className="icon-2 w-icon-dropdown-toggle" aria-hidden="true"></div>
            </div>
            <nav
              className={`dropdown-list paper-type w-dropdown-list ${openDropdown === 0 ? 'w--open' : ''}`}
              id="w-dropdown-list-0"
              aria-labelledby="w-dropdown-toggle-0"
              style={{ display: openDropdown === 0 ? 'block' : 'none' }}
            >
              <div className="form-block-filter w-form">
                <form id="email-form-3" name="email-form-3" data-name="Email Form 3" className="form-3">
                  {PaperTypes.map((paper, index) => {
                    const isChecked = selectedPapers.includes(paper.label)
                    return (
                      <div className="div-block-45" key={index}>
                        <label className="w-checkbox checkbox-field line-item-filter-checkbox">
                          <div className={`w-checkbox-input w-checkbox-input--inputType-custom checkbox ${isChecked ? 'w--redirected-checked' : ''}`}>
                          </div>
                          <input
                            type="checkbox"
                            id={`checkbox-7-${index}`}
                            name="checkbox-7"
                            data-name="Checkbox 7"
                            checked={isChecked}
                            onChange={(e) => handlePaperCheckboxChange(paper.label, e.target.checked)}
                            style={{ opacity: 0, position: "absolute", zIndex: -1 }}
                          />
                          <span className="w-form-label">{paper.label}</span>
                        </label>
                        <div className="dot bigger paper">
                          <div>{paper.code}</div>
                        </div>
                      </div>
                    )
                  })}
                </form>
              </div>
              <div className="container-info-filter bg" onClick={() => { resetAllFilters('resetPapers') }} style={{ cursor: 'pointer' }}>
                <div className="icon small right-margin x2 w-embed">
                  <RestartIcon />
                </div>
                <div>Reset All</div>
              </div>
            </nav>
          </div>

          <div className="grid-vertical-line"></div>
          {/* Service Dropdown */}
          <div
            ref={el => dropdownRefs.current[1] = el}
            data-hover="false"
            data-delay="0"
            className={`w-dropdown ${openDropdown === 1 ? 'w--open' : ''}`}
          >
            <div
              className="filter-select-field w-dropdown-toggle"
              id="w-dropdown-toggle-1"
              aria-controls="w-dropdown-list-1"
              aria-haspopup="menu"
              aria-expanded={openDropdown === 1}
              role="button"
              tabIndex="0"
              onClick={() => toggleDropdown(1)}
            >
              <div>Products</div>
              <div className="icon-2 w-icon-dropdown-toggle" aria-hidden="true"></div>
            </div>
            <nav
              className={`dropdown-list service w-dropdown-list ${openDropdown === 1 ? 'w--open' : ''}`}
              id="w-dropdown-list-1"
              aria-labelledby="w-dropdown-toggle-1"
              style={{ display: openDropdown === 1 ? 'block' : 'none' }}
            >
              <div className="form-block-filter w-form">
                <form id="email-form-3" name="email-form-3" data-name="Email Form 3" className="form-3">
                  {Services.map((service, index) => {
                    const isChecked = selectedServices.includes(service.value)
                    return (
                      <div className="div-block-45" key={index}>
                        <label className="w-checkbox checkbox-field line-item-filter-checkbox">
                          <div className={`w-checkbox-input w-checkbox-input--inputType-custom checkbox ${isChecked ? 'w--redirected-checked' : ''}`}></div>
                          <input
                            type="checkbox"
                            id={`checkbox-service-${index}`}
                            name="checkbox-service"
                            data-name="Checkbox Service"
                            checked={isChecked}
                            onChange={(e) => handleServiceCheckboxChange(service.value, e.target.checked)}
                            style={{ opacity: 0, position: "absolute", zIndex: -1 }}
                          />
                          <span className="w-form-label">{service.label}</span>
                        </label>
                        <div className={`dot bigger ${service.className}`}>
                          <div>{service.code}</div>
                        </div>
                      </div>
                    )
                  })}
                </form>
              </div>
              <div className="container-info-filter bg" onClick={() => resetAllFilters('resetServices')} style={{ cursor: 'pointer' }}>
                <div className="icon small right-margin x2 w-embed">
                  <RestartIcon />
                </div>
                <div>Reset All</div>
              </div>
            </nav>
          </div>
        </div>
      </div>
    </div>
  )
}

export default LineItemFilters
